import { Group, Stack, useMantineTheme } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
	fetchApiMemberEnv,
	fetchCreateIntent,
	useReactivateOrCreateSecodaLiteSubscription,
} from '@repo/api-codegen';
import { useGetBillingInfo } from '@repo/api-codegen/v2';
import { Text, TextSkeleton, Title } from '@repo/foundations';
import { loadStripe } from '@stripe/stripe-js';
import { IconCheck } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { AuthCard } from '../../components/Auth/AuthCard';
import { CompanyNamesCarousel } from '../../components/CompanyNamesCarousel';
import { StripePaymentMethodModal } from '../../components/Settings/StripePaymentMethodModal';

interface OnboardingSubscriptionControllerProps {
	title?: string;
	subtitle?: string;
	priceText?: string;
	cancelText?: string;
	features?: string[];
	onSuccess?: () => void;
	onCancel?: () => void;
	showCompanyCarousel?: boolean;
	width?: string;
}

export function OnboardingSubscriptionController({
	title = 'Start your 7 day free trial',
	subtitle = 'Start automating data questions with a powerful Al assistant built for data',
	priceText = '$20 per user/month after trial',
	cancelText = 'Cancel your trial at anytime',
	features = [
		'$20 in AI credits per month',
		'Access to Data warehouse, database, and transformation integrations',
		'Q&A management',
		'Slack integration',
		'AI personas and filters',
		'Light data catalog',
		'Data glossary',
		'Data documentation',
		'SSO',
	],
	onSuccess,
	onCancel,
	showCompanyCarousel = true,
	width = '800px',
}: OnboardingSubscriptionControllerProps) {
	const theme = useMantineTheme();
	const [subscriptionError, setSubscriptionError] = useState<Error | null>(
		null
	);

	const { data: billingInfo } = useGetBillingInfo({});

	const { mutateAsync: reactivateOrCreateSecodaLiteSubscription } =
		useReactivateOrCreateSecodaLiteSubscription();

	const {
		data: paymentConfig,
		isLoading: isLoadingPaymentConfig,
		error: paymentConfigError,
	} = useQuery({
		queryKey: ['subscription-payment-config'],
		queryFn: async () => {
			const publicEnv = await fetchApiMemberEnv({});
			const setupIntent = await fetchCreateIntent({
				body: {
					return_url: window.location.href,
				},
			});

			if (!publicEnv?.STRIPE_PUBLISHABLE_KEY || !setupIntent?.client_secret) {
				return null;
			}

			const stripeInstance = loadStripe(publicEnv.STRIPE_PUBLISHABLE_KEY);

			return {
				type: setupIntent.type,
				stripeInstance,
				clientSecret: setupIntent.client_secret,
			};
		},
		meta: { normalize: false },
	});

	const [isLoading] = useDebouncedValue(isLoadingPaymentConfig, 1000);

	const handlePaymentSuccess = useCallback(async () => {
		try {
			const status = billingInfo?.subscription_status;

			// For past_due, incomplete, incomplete_expired - retry existing subscription payment
			// The payment method update should automatically retry the subscription
			if (
				['past_due', 'incomplete', 'incomplete_expired'].includes(status || '')
			) {
				// Payment method update will automatically retry the subscription
				// No need to create a new subscription
				onSuccess?.();
			} else {
				// For new subscriptions or other cases, create a new subscription
				await reactivateOrCreateSecodaLiteSubscription({});
				onSuccess?.();
			}
		} catch (error) {
			setSubscriptionError(error as Error);
		}
	}, [
		reactivateOrCreateSecodaLiteSubscription,
		onSuccess,
		billingInfo?.subscription_status,
	]);

	return (
		<AuthCard width={width} p="0">
			<Group noWrap align="stretch" spacing="0">
				<Stack
					spacing={0}
					pr={0}
					justify="space-between"
					style={{
						flex: 1,
					}}
				>
					<Stack
						p="2xl"
						bg={theme.other.getColor('surface/secondary/default')}
						spacing="3xs"
						h="100%"
						justify="flex-start"
					>
						<Title>{title}</Title>
						<Text color="text/secondary/default">{subtitle}</Text>
						<Text size="lg" fw={600} mt="md">
							{priceText}
						</Text>
						<Text size="sm" fw={400}>
							{cancelText}
						</Text>
						<Stack spacing="xs" mt="xl">
							<Text size="sm" fw={500}>
								Includes:
							</Text>
							{features.map((feature) => (
								<Group key={feature} spacing="xs" noWrap>
									<IconCheck
										style={{ minWidth: '16px' }}
										size={16}
										color={theme.other.getColor('icon/secondary/default')}
									/>
									<Text size="sm">{feature}</Text>
								</Group>
							))}
						</Stack>
						{showCompanyCarousel && (
							<Stack maw="300px" spacing="md" mt="2xl">
								<CompanyNamesCarousel />
							</Stack>
						)}
					</Stack>
				</Stack>
				<Stack
					maw="500px"
					p="2xl"
					justify="space-between"
					style={{
						flex: 1,
						backgroundColor: 'var(--mantine-color-gray-0)',
					}}
				>
					{isLoading ||
						(!paymentConfig && <TextSkeleton height={400} width="100%" />)}
					{!isLoading && paymentConfig && (
						<StripePaymentMethodModal
							type={paymentConfig.type}
							clientSecret={paymentConfig?.clientSecret}
							stripeInstance={paymentConfig?.stripeInstance}
							onSuccess={handlePaymentSuccess}
							onCancel={onCancel}
						/>
					)}
					{paymentConfigError && (
						<Text color="text/critical/default">
							{paymentConfigError.message}
						</Text>
					)}
					{subscriptionError && (
						<Text color="text/critical/default">
							{subscriptionError.message}
						</Text>
					)}
				</Stack>
			</Group>
		</AuthCard>
	);
}
